import { useRef, useState } from "react";
import { Upload, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "../../lib/api";
import { Input, ChannelSelect } from "./AdminInput";

const BLANK = { channel_id: "", title: "", description: "", file: null, thumbnail: null, progress: 0, loading: false };

export const UploadForm = ({ channels, uploadToken, onUploaded, title = "Téléverser une vidéo" }) => {
  const [u, setU] = useState(BLANK);
  const fileRef = useRef(null);
  const thumbRef = useRef(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!u.channel_id || !u.title.trim() || !u.file) { toast.error("Chaîne, titre et fichier requis"); return; }
    setU(s => ({ ...s, loading: true, progress: 0 }));
    try {
      const fd = new FormData();
      fd.append("title", u.title);
      fd.append("channel_id", u.channel_id);
      fd.append("description", u.description);
      fd.append("file", u.file);
      if (u.thumbnail) fd.append("thumbnail", u.thumbnail);
      if (uploadToken) fd.append("upload_token", uploadToken);
      await api.post("/videos/upload", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (evt) => { if (evt.total) setU(s => ({ ...s, progress: Math.round((evt.loaded / evt.total) * 100) })); },
      });
      toast.success(`Vidéo téléversée · ${u.title}`);
      setU({ ...BLANK, channel_id: u.channel_id });
      if (fileRef.current) fileRef.current.value = "";
      if (thumbRef.current) thumbRef.current.value = "";
      onUploaded?.();
    } catch (err) {
      toast.error(apiError(err, "Échec du téléversement"));
      setU(s => ({ ...s, loading: false, progress: 0 }));
    }
  };

  return (
    <form onSubmit={submit} className="glass rounded-xl p-5 space-y-3" data-testid="upload-form">
      <h3 className="font-display text-xl flex items-center gap-2"><Upload className="w-4 h-4 text-[color:var(--accent)]" /> {title}</h3>
      <p className="text-xs text-[color:var(--text-muted)]">MP4 · WebM · MOV (max 500 Mo). Stocké sur le serveur Fedevo.</p>
      <ChannelSelect channels={channels} value={u.channel_id} onChange={v => setU({ ...u, channel_id: v })} testid="upload-channel" />
      <Input label="Titre" value={u.title} onChange={v => setU({ ...u, title: v })} required testid="upload-title" />
      <Input label="Description (optionnelle)" value={u.description} onChange={v => setU({ ...u, description: v })} testid="upload-desc" />
      <label className="block text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Fichier vidéo
        <input ref={fileRef} data-testid="upload-file" type="file" accept="video/mp4,video/webm,video/quicktime,video/x-m4v" required onChange={e => setU({ ...u, file: e.target.files?.[0] || null })}
          className="mt-1 w-full text-xs file:mr-3 file:py-2 file:px-4 file:rounded-full file:border-0 file:bg-[color:var(--accent)] file:text-black file:font-semibold file:cursor-pointer file:uppercase file:tracking-widest file:text-[10px]" />
      </label>
      {u.file && <div className="text-xs text-[color:var(--text-muted)] font-mono">{u.file.name} · {(u.file.size / (1024 * 1024)).toFixed(1)} Mo</div>}
      <label className="block text-xs uppercase tracking-widest text-[color:var(--text-muted)]">Miniature (optionnelle)
        <input ref={thumbRef} data-testid="upload-thumb" type="file" accept="image/png,image/jpeg,image/webp,image/gif" onChange={e => setU({ ...u, thumbnail: e.target.files?.[0] || null })}
          className="mt-1 w-full text-xs file:mr-3 file:py-1.5 file:px-3 file:rounded-full file:border file:border-[color:var(--border)] file:bg-[color:var(--bg-elev)] file:text-[color:var(--text-muted)] file:cursor-pointer file:uppercase file:tracking-widest file:text-[10px] hover:file:border-[color:var(--accent)] hover:file:text-[color:var(--accent)]" />
      </label>
      {u.thumbnail && (
        <div className="flex items-center gap-3">
          <img src={URL.createObjectURL(u.thumbnail)} alt="" className="w-16 h-10 object-cover rounded" />
          <div className="text-xs text-[color:var(--text-muted)] font-mono">{u.thumbnail.name} · {(u.thumbnail.size / 1024).toFixed(0)} Ko</div>
        </div>
      )}
      {u.loading && (
        <div className="w-full h-2 rounded-full bg-[color:var(--bg-elev-2)] overflow-hidden" data-testid="upload-progress">
          <div className="h-full bg-[color:var(--accent)] transition-all" style={{ width: `${u.progress}%` }} />
        </div>
      )}
      <button data-testid="upload-submit" disabled={u.loading} className="inline-flex items-center gap-2 bg-[color:var(--accent)] text-black font-semibold px-5 py-2 rounded-full text-xs uppercase tracking-widest disabled:opacity-50">
        {u.loading ? <><Loader2 className="w-4 h-4 animate-spin" /> {u.progress}%</> : <><Upload className="w-4 h-4" /> Téléverser</>}
      </button>
    </form>
  );
};
